'use client'

import React, { useState, useRef, useMemo } from 'react'
import Image from 'next/image'
import NumericInput from '@components/CalculatorInput/NumericInput'
import MultiSelect from '@components/CalculatorInput/MultiSelect'
import SliderInput from '@components/CalculatorInput/SliderInput'
import EnterpriseBreadcrumb from '@components/EnterpriseBreadcrumb/enterpriseBreadcrumb'
import BusinessModals from '@components/BusinessModal/businessModals'
import {
  preprocessPricingGrid,
  calculatePremium,
  formatCurrency,
  type ProcessedPricingGrid,
  type CalculatorState,
} from '@helpers/enterprisePriceCalculator'
import { businessTick } from 'src/businessLandingPageMock'
import styles from './calculator.module.scss'

const CALCULATOR_INPUT_COMPONENTS = {
  'calculator-input.slider-input': SliderInput,
  'calculator-input.numeric-input': NumericInput,
  'calculator-input.multi-select': MultiSelect,
}

const Calculator = ({ enterprisePriceCalculatorResponse }) => {
  const rawPricingGrid = enterprisePriceCalculatorResponse?.attributes?.pricingGrid || null
  const calculatorInputs = enterprisePriceCalculatorResponse?.attributes?.calculatorInputs || []
  const { pageTitle, mobileText, ctaText, ctaBannerText, ctaFormSnippet, offerText } =
    enterprisePriceCalculatorResponse?.attributes

  // Preprocess pricing grid once on mount
  const processedPricingGrid = useMemo<ProcessedPricingGrid | null>(
    () => preprocessPricingGrid(rawPricingGrid),
    [rawPricingGrid]
  )

  // Find inputs by name field for display purposes
  const coverageTypeInput = calculatorInputs.find((inp: any) => inp.name === 'coverageType')
  const maternityInput = calculatorInputs.find((inp: any) => inp.name === 'additionalCoverage')
  const roomRentInput = calculatorInputs.find((inp: any) => inp.name === 'roomRent')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleClickCta = () => setIsModalOpen(true)

  // Initialize state from Strapi defaultValues using name field
  const initializeState = (): CalculatorState => {
    const initialState: CalculatorState = {}
    calculatorInputs.forEach((data: any) => {
      const inputName = data.name
      if (!inputName) return

      const relation = data.__component
      if (relation === 'calculator-input.slider-input') {
        initialState[inputName] = data.defaultValue || data.minValue || 0
      } else if (relation === 'calculator-input.numeric-input') {
        initialState[inputName] = data.defaultValue || data.min || 0
      } else if (relation === 'calculator-input.multi-select') {
        initialState[inputName] = data.defaultSelected || null
      }
    })
    return initialState
  }

  const [calculatorState, setCalculatorState] = useState<CalculatorState>(initializeState)
  const ctaSectionRef = useRef<HTMLDivElement>(null)

  // Calculate premium per employee using the utility function
  const premiumPerEmployee = useMemo(
    () => calculatePremium(calculatorState, processedPricingGrid),
    [calculatorState, processedPricingGrid]
  )

  // Handle input changes
  const handleInputChange = (inputName: string, value: any) => {
    setCalculatorState(prev => ({
      ...prev,
      [inputName]: value,
    }))
  }

  // Get values using name field
  const employeeCount = Number(calculatorState.employeeCount || 0)
  const annualSpend = premiumPerEmployee * employeeCount
  const coveragePerEmployee = Number(calculatorState.sumInsured || 0)

  // Get selected options for display
  const hasMaternity = Boolean(calculatorState.additionalCoverage)
  const roomType = String(calculatorState.roomRent || '')

  // Scroll to CTA section handler
  const handleScrollToCta = () => {
    ctaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className={styles.outerWrapper}>
      <div className={styles.labelAndInputsContainer}>
        <div className={styles.gap30}>
          <EnterpriseBreadcrumb data={enterprisePriceCalculatorResponse?.attributes?.breadcrumbs} />
          <div className={styles.gap8}>
            <p className={styles.title}>{pageTitle}</p>
            <p className={styles.mobileText} dangerouslySetInnerHTML={{ __html: mobileText }} />
          </div>
        </div>
        <div className={styles.calculatorInputsContainer}>
          {enterprisePriceCalculatorResponse?.attributes?.calculatorInputs?.map((data: any, index: number) => {
            const relation = data.__component
            const Component = CALCULATOR_INPUT_COMPONENTS[relation]
            const inputName = data.name
            const value = inputName ? calculatorState[inputName] : undefined

            if (Component && inputName) {
              return (
                <Component
                  key={inputName || index}
                  data={data}
                  value={value}
                  onChange={(newValue: any) => handleInputChange(inputName, newValue)}
                />
              )
            }
            return null
          })}
        </div>
      </div>
      <div ref={ctaSectionRef} className={styles.summaryContainer}>
        <div className={styles.gap20}>
          <div>
            <p className={styles.annualPremiumLabel}>Annual premium starting at</p>
            <p className={styles.annualPremiumValue}>{formatCurrency(premiumPerEmployee)}</p>
            <p className={styles.annualPremiumDescriptor}>per employee</p>
          </div>

          <div className={`${styles.summaryCard} ${styles.gap12}`}>
            <p className={styles.cardHeading}>Annual spend</p>
            <div className={styles.gap4}>
              <p className={styles.cardValue}>{formatCurrency(annualSpend)}</p>
              <p className={styles.cardDescriptor}>for {employeeCount} employees</p>
            </div>
          </div>

          <div className={`${styles.summaryCard} ${styles.gap16}`}>
            <div className={styles.gap16}>
              <div className={styles.cardHeading}>
                <span>Coverage per employee</span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(coveragePerEmployee)}</span>
              </div>
              <div className={styles.cardHeading}>
                <span>Protecting</span>
                <span style={{ fontWeight: 600 }}>
                  {
                    coverageTypeInput?.optionCards?.find((option: any) => option.value === calculatorState.coverageType)
                      ?.optionCardHeading
                  }
                </span>
              </div>
              <div className={styles.featureChips}>
                {hasMaternity && (
                  <div className={styles.featureChip}>
                    <div className={styles.coverageCheckIcon}>{businessTick}</div>
                    {
                      maternityInput?.optionCards?.find(
                        (option: any) => option.value === calculatorState.additionalCoverage
                      )?.optionCardHeading
                    }
                  </div>
                )}
                {roomType && (
                  <div className={styles.featureChip}>
                    <div className={styles.coverageCheckIcon}>{businessTick}</div>
                    {roomRentInput?.optionCards?.find((option: any) => option.value === roomType)?.optionCardHeading}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.ctaSection}>
          <div className={styles.ctaPrompt}>{ctaBannerText}</div>
          <div className={styles.offer}>
            <div style={{ marginTop: '4px', marginBottom: '4px' }}>{offerText}</div>
            <button type="button" className={styles.primaryButton} onClick={handleClickCta}>
              {ctaText}
            </button>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute' }}>
        <div className={styles.stickyMobileCta}>
          <div className={styles.stickyMobileCtaContent}>
            <div className={styles.stickyMobileCtaLeft}>
              <p className={styles.stickyMobileCtaLabel}>Annual premium starts from</p>
              <div className={styles.stickyMobileCtaPrice}>
                <span>{formatCurrency(premiumPerEmployee)}</span>
                <button type="button" className={styles.stickyMobileCtaChevron} onClick={handleScrollToCta}>
                  <Image
                    src="https://acko-cms.ackoassets.com/chevron_683239dadb.png"
                    alt="chevron"
                    width={24}
                    height={24}
                  />
                </button>
              </div>
              <p className={styles.stickyMobileCtaDescriptor}>Per employee</p>
            </div>
            <button type="button" className={styles.stickyMobileCtaButton} onClick={handleClickCta}>
              {ctaText}
            </button>
          </div>
        </div>
        <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={ctaFormSnippet} />
      </div>
    </div>
  )
}

export default Calculator
